import { createBrowserHistory } from 'history'
import { EmployeeDetails } from '../services/EmployeeService'
import { createPathWithLang } from './languagePathUtils'
import { NetworkConstants } from './NetworkConstants'

interface HistoryState {
  stack: string[]
  prevPath: string
}

// Sanitises any string before it goes back into sessionStorage. We only ever store
// in-app, same-origin paths (e.g. "/en/admin/dashboard?foo=bar"), so we restrict the
// allowed character set to the printable URL-safe subset and reject protocol-relative
// URLs ("//evil.com"), schemed URLs, and values over a sensible length cap.
const MAX_PATH_LENGTH = 2048
const SAFE_PATH_CHARS = /^[A-Za-z0-9._~!$&'()*+,;=:@%/?-]+$/
const isSafePath = (value: unknown): value is string => {
  if (typeof value !== 'string') return false
  if (value.length === 0 || value.length > MAX_PATH_LENGTH) return false
  if (value.startsWith('//')) return false
  if (!value.startsWith('/')) return false
  return SAFE_PATH_CHARS.test(value)
}

const sanitizePath = (value: unknown): string => (isSafePath(value) ? value : '')

const sanitizeStack = (value: unknown): string[] => (Array.isArray(value) ? value.filter(isSafePath) : [])

// When the app is opened in a new tab via target="_blank", sessionStorage is fresh and
// historyState.prevPath is empty. Fall back to document.referrer (same-origin only) so
// BackLink can still resolve to the page that linked here.
const referrerSamePath = (): string => {
  if (!document.referrer) return ''
  try {
    const referrerUrl = new URL(document.referrer)
    if (referrerUrl.origin !== globalThis.location.origin) return ''
    return sanitizePath(referrerUrl.pathname + referrerUrl.search)
  } catch {
    return ''
  }
}

const parseStoredStack = (raw: string | null): string[] => {
  if (raw === null) return []
  try {
    return sanitizeStack(JSON.parse(raw))
  } catch {
    return []
  }
}

const loadHistoryState = (): HistoryState => {
  const state: HistoryState = {
    stack: parseStoredStack(sessionStorage.getItem('sessionHistory')),
    prevPath: sanitizePath(sessionStorage.getItem('prevPath')) || referrerSamePath(),
  }
  if (state.stack.length <= 0) {
    state.stack.push(globalThis.location.pathname + globalThis.location.search)
  }
  return state
}

const saveHistoryState = (historyState: HistoryState) => {
  sessionStorage.setItem('sessionHistory', JSON.stringify(sanitizeStack(historyState.stack)))
  sessionStorage.setItem('prevPath', sanitizePath(historyState.prevPath))
}

const getDefaultRoute = (): string => {
  const userJSON = localStorage.getItem('user')
  if (userJSON) {
    const { role } = JSON.parse(userJSON) as EmployeeDetails
    if (role === 'admin') {
      return createPathWithLang(NetworkConstants.URL_REVIEW_LIST)
    } else {
      return createPathWithLang(NetworkConstants.URL_TECH_PROFILE)
    }
  }
  return createPathWithLang(NetworkConstants.URL_REVIEW_LIST)
}

const history = createBrowserHistory()
export const historyState: HistoryState = loadHistoryState()

// Maximum number of elementes allowed in the stack
const MAX_ELEMENTS_STACK = 50

// method to go back in path history
// forcePath is used to force goBack to go to a specific path
// this is because some pages like /thanks/ don't work properly with default behaviours
export function goBack(forcePath = '') {
  if (forcePath) {
    historyState.prevPath = forcePath
  } else {
    historyState.stack = historyState.stack.slice(0, historyState.stack.lastIndexOf(historyState.prevPath))
  }
  saveHistoryState(historyState)
  if (historyState.prevPath) {
    history.push(historyState.prevPath)
  } else {
    history.push(getDefaultRoute())
  }
}

// Listen window load event
globalThis.onload = () => {
  if (historyState.stack.length <= 0) {
    historyState.stack.push(globalThis.location.pathname + globalThis.location.search)
  }
  saveHistoryState(historyState)
}

history.listen(({ pathname, search }, action) => {
  const fullPath = pathname + search
  switch (action) {
    case 'POP':
      historyState.stack.pop()
      historyState.prevPath = historyState.stack.at(-2) ?? fullPath
      break
    case 'PUSH': {
      // Avoids repetitions in the stack
      const lastPath = historyState.stack.at(-1) ?? fullPath
      const repeated = fullPath.includes(lastPath) || lastPath.includes(fullPath)
      if (!repeated && !lastPath.includes('employee-review')) {
        historyState.prevPath = lastPath
      }
      historyState.stack.push(fullPath)
      break
    }
    case 'REPLACE':
      historyState.stack = [...historyState.stack.slice(0, -1), fullPath]
      break
  }

  // Emptys the stack if it has too many elements
  if (historyState.stack.length > MAX_ELEMENTS_STACK) {
    historyState.stack = historyState.stack.slice(historyState.stack.length - 10, historyState.stack.length)
  }

  if (historyState.stack.length <= 0) {
    historyState.stack.push(fullPath)
  }

  // Avoid prevPath to point to the same url
  const { prevPath, stack } = historyState
  const repeatedUrl = fullPath.includes(prevPath) || prevPath.includes(fullPath)
  if (repeatedUrl) {
    const newPrevPath =
      stack
        .filter(url => !url.includes('employee-review') && !(url.includes(prevPath) || prevPath.includes(url)))
        .at(-1) ?? getDefaultRoute()
    historyState.prevPath = newPrevPath
  }

  saveHistoryState(historyState)
})

export default history
